import { useEffect } from 'react';
import type { ProjectTask } from '@/types/odoo';
import { useProjectViewStore } from '@/stores/projectView';
import { usePermissionsStore } from '@/stores/permissions';
import { PartnerWarningBanner } from '@/components/ui/PartnerWarningBanner';
import { buildFaseGroups } from '@/utils/materiaal';
import { MateriaalFaseGroup } from '@/components/project/materiaal/MateriaalFaseGroup';
import { MateriaalCadModals } from '@/components/project/materiaal/MateriaalCadModals';
import { useMateriaalFaseControls } from '@/hooks/useMateriaalFaseControls';

interface MateriaalTabProps {
  projectId: number;
  projectTasks: ProjectTask[];
  isEasykit?: boolean;
}

export function MateriaalTab({ projectId, projectTasks, isEasykit }: MateriaalTabProps) {
  const partnerWarning = useProjectViewStore((s) => s.detail?.partnerWarning);
  const canFeature = usePermissionsStore((s) => s.canFeature);
  const canUpload = canFeature('csv_upload');
  const canToggleOpgelijst = canUpload;

  // Shared orchestration — opgelijst toggle (+ revert), CAD flow, upload, delete.
  const controls = useMateriaalFaseControls(projectId, projectTasks);

  const {
    materiaalLines,
    materiaalPickings,
    materiaalMoves,
    materiaalProjectId,
    materiaalLoading,
    materiaalError,
    fetchMateriaallijst,
    uploadingMateriaal,
    uploadError,
  } = useProjectViewStore();

  const dismissUploadError = () => {
    useProjectViewStore.setState({ uploadError: null });
  };

  useEffect(() => {
    if ((materiaalLines === null || materiaalProjectId !== projectId) && !materiaalLoading) {
      fetchMateriaallijst(projectId);
    }
  }, [materiaalLines, materiaalProjectId, materiaalLoading, projectId, fetchMateriaallijst]);

  if (materiaalLoading && !uploadingMateriaal) {
    return (
      <div className="text-center py-12">
        <div className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
          <span className="sr-only">Laden...</span>
        </div>
        <p className="text-td-muted text-sm mt-3">Materiaallijsten laden...</p>
      </div>
    );
  }

  if (materiaalError) {
    return (
      <div className="mx-4 lg:mx-0 bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
        <p className="font-semibold">Fout bij laden</p>
        <p className="text-sm mt-1">{materiaalError}</p>
      </div>
    );
  }

  const fases = buildFaseGroups(materiaalPickings ?? [], materiaalLines ?? [], projectTasks, materiaalMoves ?? []);
  const totalPickings = materiaalPickings?.length ?? 0;

  return (
    <div>
      {partnerWarning && <PartnerWarningBanner message={partnerWarning} />}

      {uploadError && (
        <div className="mb-4 bg-red-50 text-red-600 p-4 rounded-xl flex items-start gap-3" role="alert">
          <div className="flex-1">
            <p className="font-semibold text-sm">Er ging iets mis</p>
            <p className="text-sm mt-0.5">{uploadError}</p>
          </div>
          <button
            type="button"
            onClick={dismissUploadError}
            className="shrink-0 text-red-400 hover:text-red-600 transition-colors"
            aria-label="Foutmelding sluiten"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className="mb-5">
        <h2 className="text-lg font-extrabold text-td-navy">Materiaallijsten</h2>
        <p className="text-sm text-td-muted mt-0.5">
          {totalPickings} {totalPickings === 1 ? 'materiaallijst' : 'materiaallijsten'} in {fases.length} {fases.length === 1 ? 'fase' : 'fases'}
        </p>
        {canUpload && (
          <p className="hidden sm:block text-xs text-td-muted mt-1">
            Tip: sleep een CSV-bestand op een fase om te uploaden.
          </p>
        )}
      </div>

      <div className="space-y-2">
        {fases.map((fase, index) => {
          const childTasks = projectTasks
            .filter((t) => t.parent_id && t.parent_id[0] === fase.taskId)
            .sort((a, b) => {
              if (!a.date_deadline && !b.date_deadline) return 0;
              if (!a.date_deadline) return 1;
              if (!b.date_deadline) return -1;
              return a.date_deadline.localeCompare(b.date_deadline);
            });
          return (
            <MateriaalFaseGroup
              key={fase.taskId}
              fase={fase}
              defaultOpen={index === 0}
              isUploading={uploadingMateriaal}
              isEasykit={isEasykit}
              childTasks={childTasks}
              getOpgelijstValue={controls.getOpgelijstValue}
              onToggleOpgelijst={controls.onToggleOpgelijst}
              onUpload={async (file) => {
                await controls.onUpload(fase.taskId, file);
              }}
              onCadClick={() => {
                controls.cad.openCad({ taskId: fase.taskId, taskName: fase.taskName });
              }}
              onDelete={controls.onDelete}
              onError={controls.onError}
              allowUpload={canUpload}
              allowOpgelijst={canToggleOpgelijst}
            />
          );
        })}
      </div>

      <MateriaalCadModals controls={controls} projectId={projectId} />
    </div>
  );
}
